// Summary: Semantic matching for the POS hamburger search — maps each tile to concept keywords
// (Hebrew synonyms, English equivalents, related terms) so "כסף", "cash" or "מגירא" all reach
// פתח מגירה. Matching itself lives in the shared semantic-search util.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-settingshubmodal
import { matchesSearchQuery, normalizeSearchText } from '../../../../utils/semantic-search.util';

export { matchesSearchQuery, normalizeSearchText };

// Keyed by SettingsHubKey. A tile with no entry still matches on its own label/subtitle.
const TILE_KEYWORDS: Record<string, string[]> = {
    posAssistant: ['ai', 'בינה מלאכותית', 'עוזר', 'שאלות', 'chat', 'צאט', 'בוט'],
    openDrawer: ['מגירה', 'כסף', 'מזומן', 'קופה', 'drawer', 'cash', 'פתיחת מגירה'],
    refresh: ['רענון', 'עדכון', 'טעינה', 'refresh', 'reload', 'sync', 'סנכרון'],
    refreshAll: ['רענון', 'כל המכשירים', 'שידור', 'גלובלי', 'broadcast', 'refresh all'],
    backoffice: ['בק אופיס', 'משרד', 'backoffice', 'admin', 'מערכת'],
    quickCharge: ['חיוב', 'תשלום', 'גבייה', 'charge', 'payment', 'מהיר'],
    dailyBrief: ['בריף', 'סיכום', 'יומי', 'brief', 'summary', 'סיכום יומי'],
    quickOrder: ['הזמנה', 'מהירה', 'order', 'quick'],
    takeAway: ['טייק אווי', 'לקחת', 'takeaway', 'take away', 'איסוף', 'משלוח'],
    kds: ['מטבח', 'מסך מטבח', 'בון', 'בונים', 'kds', 'kitchen', 'הכנה'],
    happyHour: ['הפי האוור', 'שעה שמחה', 'מבצע', 'הנחה', 'happy hour', 'promotion'],
    session: ['סגירת יום', 'סוף יום', 'סגירה', 'משמרת', 'z', 'close', 'shift'],

    reportX: ['דוח', 'x', 'איקס', 'מכירות', 'report'],
    restoreZ: ['שחזור', 'z', 'הדפסה מחדש', 'דוח', 'restore'],
    workerReport: ['עובד', 'מלצר', 'worker', 'employee', 'דוח עובד'],
    itemsReport: ['פריטים', 'מוצרים', 'מנות', 'מכירות', 'items', 'products'],
    employeeAttendanceReport: ['נוכחות', 'שעות', 'שעון', 'attendance', 'hours'],
    zReportLayout: ['מודולרי', 'עיצוב', 'סידור', 'layout', 'דוח'],
    zcreditTransactions: ['אשראי', 'עסקאות', 'credit', 'zcredit', 'סליקה'],
    accountantReport: ['רואה חשבון', 'הנהלת חשבונות', 'accountant', 'מס', 'דוח'],

    areasEdit: ['אזורים', 'שולחנות', 'מפה', 'עריכה', 'areas', 'tables', 'floor'],
    pastReceipts: ['קבלות', 'חשבוניות', 'היסטוריה', 'receipts', 'invoices'],
    attendance: ['נוכחות', 'שעון', 'כניסה', 'יציאה', 'attendance', 'clock'],
    attendanceRules: ['נוכחות', 'כללים', 'הגדרות', 'rules'],
    employees: ['עובדים', 'מלצרים', 'צוות', 'employees', 'staff', 'users'],
    products: ['מוצרים', 'תפריט', 'מנות', 'פריטים', 'products', 'menu'],
    categories: ['קטגוריות', 'קבוצות', 'תפריט', 'categories'],
    autoCloseSession: ['סגירה אוטומטית', 'אוטומטי', 'תזמון', 'auto close', 'schedule'],
    openingCash: ['מזומן', 'פתיחה', 'קופה', 'opening cash', 'float'],
    cashMove: ['מזומן', 'הכנסה', 'הוצאה', 'תנועות', 'cash', 'move'],
    retroCountedCash: ['מזומן', 'ספירה', 'רטרו', 'נספר', 'count'],
    minOrder: ['מינימום', 'הזמנה', 'minimum', 'min'],
    kioskMode: ['קיוסק', 'kiosk', 'מצב'],
    fixedPrinter: ['מדפסת', 'קיבוע', 'ברירת מחדל', 'printer', 'fixed'],
    stationarySetup: ['עמדה', 'נייחת', 'תחנה', 'station', 'stationary'],
    restigo: ['רסטיגו', 'restigo', 'ממשק', 'אינטגרציה'],
    areaPrintSettings: ['הדפסה', 'אזורית', 'מדפסות', 'print', 'routing'],
    posSettings: ['הגדרות', 'settings', 'קונפיגורציה', 'אפשרויות', 'config'],

    agents: ['סוכן', 'סוכנים', 'agent', 'מדפסת', 'חיבור'],
    printers: ['מדפסות', 'הדפסות', 'printers', 'הדפסה', 'תור הדפסה'],
    testPush: ['פוש', 'התראות', 'push', 'notifications', 'בדיקה'],
    vatRate: ['מעמ', 'מס', 'vat', 'אחוז', 'tax'],
    headerEdit: ['כותרת', 'header', 'לוגו', 'עסק', 'קבלה'],
};

export const buildTileHaystack = (key: string, label: string, subtitle?: string): string[] => [
    label,
    ...(subtitle ? [subtitle] : []),
    ...(TILE_KEYWORDS[key] ?? []),
].map(normalizeSearchText);
